import { JobDtoType } from '../domain/jobs';
import { IEventRequestParams, IPaginationParams, ISortRequestParams } from './shared.dto';
import { IAttendeeDTO } from './attendees.dao';
import { IUserDTO } from './user-groups.dao';

abstract class IOrganizationsDAO {
    public abstract getOrganizations(params: IIgnoreAttendeesRequest & IEventRequestParams
        | ISortRequestParams<TGetOrganizationsSortKeys>
        | IPaginationParams)
        : Promise<IOrganizationsListDTO>;
    public abstract getAllOrganizations(params: IIgnoreAttendeesRequest & IEventRequestParams): Promise<IOrganizationsListDTO>;
    public abstract createOrganization(params: ICreateOrganizationDTO & IEventRequestParams): Promise<void>;
    public abstract updateOrganization(params: IUpdateOrganizationDTO & IEventRequestParams): Promise<void>;
    public abstract getOrganizationDetails(params: IEventRequestParams & { organizationId: number }): Promise<IOrganizationDetailsDTO>;
    public abstract importOrganizations(params: IEventRequestParams & { file: File }): Promise<JobDtoType>;
    public abstract getOrganizationsCSV(params: IEventRequestParams): Promise<void>;
    public abstract deleteOrganization(params: IDeleteOrganizationDTO): Promise<JobDtoType>;
    public abstract getOrganizationsLinksCSV(params: IEventRequestParams): Promise<void>;
    public abstract getChaperones(params: IEventRequestParams): Promise<IUserDTO[]>;
    public abstract getOrganizationTypes(params: IEventRequestParams): Promise<IOrganizationTypesDTO>;
}

interface IOrganizationImportDetailsDTO {
    externalOrganizationId: string;
    attendeeOrganizationName : string;
    attendeeOrganizationType : string;
    attendeeOrganizationTicker : string;
    externalAttendeeId: string;
    attendeeFirstName : string;
    attendeeLastNameName : string;
    attendeeEmailName : string;
    attendeeSalutation : string;
    attendeePhoneNumber : string;
    attendeeTitle : string;
}

interface ICreateOrganizationDTO {
    name: string;
    organizationTicker: string;
    organizationType: string;
    externalOrganizationId: string;
    approvedDomains?: {domain: string}[];
    participantLink?: string;
    presenterLink?: string;
    chaperones?: IUserDTO[];
    priorityInEvent?: number;
}

interface IUpdateOrganizationDTO extends ICreateOrganizationDTO {
    organizationId: number;
}

interface IOrganizationDetailsDTO {
    meetingOrganizationId?: number;
    externalOrganizationId?: string;
    name: string;
    organizationId: number;
    attendees: IAttendeeDTO[];
    organizationType?: string;
    organizationTicker?: string;
    approvedDomains?: {domain: string}[];
    participantLink?: string;
    presenterLink?: string;
    chaperones?: IUserDTO[];
    priorityInEvent?: number;
    registrantsCount?: number;
}

interface IIgnoreAttendeesRequest {
    ignoreAttendees: boolean;
};

interface IOrganizationsListDTO {
    content: IOrganizationDetailsDTO[];
    totalElements: number;
    totalPages: number;
}

interface IDeleteOrganizationDTO extends IEventRequestParams {
    organizationId: number;
}

type TGetOrganizationsSortKeys = keyof Omit<IOrganizationDetailsDTO, 'organizationId'> | 'organizationName' | 'organizationType' | 'organizationTicker';

interface IOrganizationTypesDTO {
    content: string[];
    totalElements: number;
}

const IGNORE_ATTENDEES = true;

export {
    IOrganizationsDAO,
    IOrganizationDetailsDTO,
    IOrganizationsListDTO,
    TGetOrganizationsSortKeys,
    ICreateOrganizationDTO,
    IUpdateOrganizationDTO,
    IOrganizationImportDetailsDTO,
    IDeleteOrganizationDTO,
    IOrganizationTypesDTO,
    IIgnoreAttendeesRequest,
    IGNORE_ATTENDEES,
};
